import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import MovieCard from "@/components/feature/MovieCard";
import ShareDialog from "@/components/feature/ShareDialog";
import usePageMeta from "@/hooks/usePageMeta";
import {
  formatViews,
  getCategoryBySlug,
  getCategorySlug,
  getMovieById,
  getRelatedMovies,
} from "@/lib/movieData";

export default function DetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const movie = getMovieById(id);
  const [shareOpen, setShareOpen] = useState(false);
  const [posterOpen, setPosterOpen] = useState(false);

  usePageMeta({
    title: movie ? `${movie.title} - 秋霞电影` : "影片详情 - 秋霞电影",
    description: movie
      ? `${movie.title}（${movie.year}，${movie.region}）${movie.summary} 导演：${movie.director}，主演：${movie.cast.join("、")}。`
      : "秋霞电影影片详情页，查看电影资料、剧情简介与相关资讯。",
    keywords: movie
      ? `${movie.title},${movie.types.join(",")},${movie.cast.join(",")},${movie.director},电影资讯,秋霞电影`
      : "电影详情,秋霞电影",
  });

  if (!movie) {
    return (
      <PageLayout>
        <div className="w-full px-4 md:px-6 py-24">
          <div className="max-w-[640px] mx-auto text-center">
            <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-file-search-line text-2xl"></i>
            </span>
            <h1 className="mt-6 font-heading text-2xl text-foreground-950">这部影片的资料暂未收录</h1>
            <p className="mt-3 text-sm text-foreground-600">
              链接可能已失效，你可以回到首页或直接浏览片库寻找其他影片。
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/"
                className="h-10 px-5 inline-flex items-center rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors"
              >
                返回首页
              </Link>
              <Link
                to="/category/drama"
                className="h-10 px-5 inline-flex items-center rounded-md border border-background-300 text-foreground-700 text-sm hover:border-primary-400 transition-colors"
              >
                浏览片库
              </Link>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  const primaryCategory = getCategoryBySlug(getCategorySlug(movie.types[0]));
  const related = getRelatedMovies(movie, 8);
  const sameType = related.filter((item) => item.types.some((type) => movie.types.includes(type))).slice(0, 4);

  const metaRows = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join(" / ") },
    { label: "类型", value: movie.types.join(" / ") },
    { label: "地区", value: movie.region },
    { label: "上映日期", value: movie.releaseDate },
    { label: "片长", value: `${movie.duration} 分钟` },
    { label: "站内热度", value: `${formatViews(movie.views)} 次浏览` },
  ];

  return (
    <PageLayout>
      <section className="relative w-full bg-primary-950 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img
            src={movie.poster}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-top blur-2xl scale-110"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/90 to-primary-950/60"></div>

        <div className="relative z-10 max-w-[1180px] mx-auto px-4 md:px-6 py-8 md:py-12">
          <nav className="flex items-center gap-2 text-xs text-primary-200 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-accent-300 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/category/drama" className="hover:text-accent-300 transition-colors">
              电影分类
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to={`/category/${getCategorySlug(movie.types[0])}`}
              className="hover:text-accent-300 transition-colors"
            >
              {movie.types[0]}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50 truncate max-w-[200px]">{movie.title}</span>
          </nav>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-[230px_1fr] lg:grid-cols-[260px_1fr] gap-7 md:gap-10">
            <div className="w-[180px] md:w-full mx-auto md:mx-0">
              <button
                type="button"
                onClick={() => setPosterOpen(true)}
                className="group relative block w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-50/15 cursor-pointer"
                aria-label={`放大查看《${movie.title}》海报`}
              >
                <img
                  src={movie.poster}
                  alt={`电影《${movie.title}》竖版海报`}
                  title={`${movie.title} - 秋霞电影`}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute right-2.5 bottom-2.5 w-8 h-8 rounded-md bg-primary-950/70 text-background-50 flex items-center justify-center backdrop-blur-sm">
                  <i className="ri-zoom-in-line"></i>
                </span>
              </button>
              <p className="mt-3 hidden md:block text-[11px] text-primary-200/60 text-center">
                点击海报可放大浏览
              </p>
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2">
                {movie.types.map((type) => (
                  <Link
                    key={type}
                    to={`/category/${getCategorySlug(type)}`}
                    className="h-7 px-3 inline-flex items-center rounded-full bg-background-50/10 border border-background-50/20 text-xs text-background-100 hover:bg-accent-500 hover:border-accent-500 transition-colors"
                  >
                    {type}
                  </Link>
                ))}
                <span className="h-7 px-3 inline-flex items-center rounded-full bg-secondary-500/25 text-xs text-secondary-100">
                  {movie.region}
                </span>
              </div>

              <h1 className="mt-4 font-heading text-3xl md:text-5xl text-background-50 leading-tight">
                {movie.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-primary-100/80">
                <span className="inline-flex items-center gap-1.5 text-accent-300">
                  <i className="ri-star-fill text-sm"></i>
                  <span className="font-medium text-base">{movie.rating.toFixed(1)}</span>
                  <span className="text-primary-200/70 text-xs">/ 10</span>
                </span>
                <span>{movie.releaseDate} 上映</span>
                <span className="hidden sm:inline">{movie.duration} 分钟</span>
                <span className="hidden sm:inline">{formatViews(movie.views)} 次浏览</span>
              </div>

              <p className="mt-5 max-w-[640px] text-sm md:text-[15px] leading-relaxed text-background-100/85">
                {movie.summary}
              </p>

              <div className="mt-4 space-y-1.5 text-sm text-primary-100/70">
                <p>
                  导演：<span className="text-background-100">{movie.director}</span>
                </p>
                <p>主演：{movie.cast.join(" / ")}</p>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line text-base"></i>
                  分享影片
                </button>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-50/30 text-background-50 text-sm hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-go-back-line text-base"></i>
                  返回来源页
                </button>
                {primaryCategory && (
                  <Link
                    to={`/category/${primaryCategory.slug}`}
                    className="inline-flex items-center gap-2 h-11 px-5 text-sm text-primary-100 hover:text-accent-300 transition-colors whitespace-nowrap"
                  >
                    更多{movie.types[0]}影片
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                )}
              </div>

              <p className="mt-5 inline-flex items-center gap-1.5 text-[11px] text-primary-200/60">
                <i className="ri-information-line"></i>
                站内仅提供影片资料与资讯展示，不含在线播放与下载资源
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100">
        <div className="max-w-[1180px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10">
            <div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
                <div className="flex items-center gap-2">
                  <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                  <h2 className="font-heading text-xl md:text-2xl text-foreground-950">剧情概述</h2>
                </div>
                <p className="mt-5 text-sm md:text-[15px] leading-[1.9] text-foreground-700">
                  {movie.synopsis}
                </p>
                <p className="mt-4 text-sm md:text-[15px] leading-[1.9] text-foreground-700">
                  影片由{movie.director}执导，{movie.cast.join("、")}共同出演，类型上融合了
                  {movie.types.join("、")}元素，整体以{movie.year}年{movie.region}电影常见的影像语言呈现。
                  故事围绕{movie.summary.replace(/。$/, "")}展开，在节奏与情绪之间保持了克制的处理方式。
                </p>

                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: "评分", value: movie.rating.toFixed(1) },
                    { label: "片长", value: `${movie.duration} 分钟` },
                    { label: "上映", value: movie.year },
                    { label: "地区", value: movie.region },
                  ].map((item) => (
                    <div key={item.label} className="rounded-md bg-background-100 px-4 py-3">
                      <p className="text-[11px] text-foreground-500">{item.label}</p>
                      <p className="mt-1 text-sm text-foreground-900">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="w-1 h-5 rounded-full bg-primary-500"></span>
                    <h2 className="font-heading text-xl md:text-2xl text-foreground-950">相关资讯</h2>
                  </div>
                  <span className="text-xs text-foreground-400">共 {movie.news.length} 条</span>
                </div>
                <ul className="mt-2 divide-y divide-background-200">
                  {movie.news.map((item) => (
                    <li key={item.title} className="py-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <h3 className="text-sm md:text-[15px] leading-relaxed text-foreground-900">
                            {item.title}
                          </h3>
                          <p className="mt-1.5 text-[11px] text-foreground-500">
                            秋霞资讯 · {item.date}
                          </p>
                        </div>
                        <span className="shrink-0 h-6 px-2 inline-flex items-center rounded bg-accent-100 text-[10px] text-accent-800">
                          电影资讯
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[11px] text-foreground-400">
                  以上资讯为演示内容，用于展示详情页的信息结构。
                </p>
              </div>
            </div>

            <aside className="flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-lg text-foreground-950">影片资料</h2>
                <dl className="mt-4 space-y-3">
                  {metaRows.map((row) => (
                    <div key={row.label} className="flex items-start gap-3">
                      <dt className="w-16 shrink-0 text-xs text-foreground-500 pt-0.5">{row.label}</dt>
                      <dd className="flex-1 text-sm text-foreground-800 leading-relaxed">{row.value}</dd>
                    </div>
                  ))}
                </dl>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="mt-5 w-full h-10 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-accent-300 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  复制链接 / 分享
                </button>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-lg text-foreground-950">同类型影片</h2>
                <ul className="mt-4 divide-y divide-background-200">
                  {sameType.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/detail/${item.id}`}
                        className="group flex items-center gap-3 py-3 cursor-pointer"
                      >
                        <div className="w-10 h-14 rounded overflow-hidden bg-background-200 shrink-0">
                          <img
                            src={item.poster}
                            alt={`《${item.title}》海报`}
                            title={`${item.title} - 秋霞电影`}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                            {item.title}
                          </p>
                          <p className="mt-1 text-[11px] text-foreground-500">
                            {item.year} · {item.region}
                          </p>
                        </div>
                        <span className="text-xs text-accent-600">{item.rating.toFixed(1)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>

          <div className="mt-14">
            <div className="flex items-end justify-between gap-3 mb-6">
              <div>
                <p className="text-[11px] tracking-[0.3em] text-foreground-400 uppercase">Recommended</p>
                <h2 className="mt-2 font-heading text-2xl text-foreground-950">相关电影推荐</h2>
              </div>
              <Link
                to={`/category/${getCategorySlug(movie.types[0])}`}
                className="text-sm text-foreground-600 hover:text-accent-600 transition-colors whitespace-nowrap"
              >
                查看更多
                <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 md:gap-x-5 gap-y-8">
              {related.slice(0, 4).map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} movieTitle={movie.title} />

      {posterOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-6">
          <div
            className="absolute inset-0 bg-primary-950/85 backdrop-blur-sm"
            onClick={() => setPosterOpen(false)}
          ></div>
          <button
            type="button"
            aria-label="关闭海报预览"
            onClick={() => setPosterOpen(false)}
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-background-50/10 text-background-50 flex items-center justify-center hover:bg-background-50/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <div className="relative max-h-full">
            <img
              src={movie.poster}
              alt={`电影《${movie.title}》放大海报`}
              title={`${movie.title} - 秋霞电影`}
              className="max-h-[80vh] w-auto rounded-lg object-contain animate-fade-in"
            />
            <p className="mt-3 text-center text-sm text-background-100">
              {movie.title} · {movie.year}
            </p>
          </div>
        </div>
      )}
    </PageLayout>
  );
}